import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Spinner } from "@/components/ds";
import { AuthContext, type AuthData } from "./hooks/useAuth";
import { SuspendedPage } from "./pages/SuspendedPage";

interface AuthGuardProps {
  readonly children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const [auth, setAuth] = useState<AuthData | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((res) => {
        if (!res.ok) {
          navigate("/login", { replace: true });
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data?.success) {
          setAuth(data.data);
        }
        setChecking(false);
      })
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, [navigate]);

  if (checking) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spinner />
      </div>
    );
  }

  if (!auth) return null;

  // Staff see the normal app (with admin access) even if their own org is
  // suspended, so the surface to repair the suspension is never locked off.
  if (auth.billingState.label === "suspended" && !auth.user.isStaff) {
    return <SuspendedPage reason={auth.org.suspensionReason} />;
  }

  return (
    <AuthContext value={auth}>
      {children}
    </AuthContext>
  );
}
